/**
 * What each bundled capability is worth, in one place.
 *
 * Every locked surface drew its own sentence, and they read like definitions:
 * *"A dated record that each agent's measured behaviour was reviewed and
 * accepted, and the baseline every later scan is compared against."* That is
 * accurate and it explains what a certificate IS. A reader who does not already
 * want one learns nothing about why they should.
 *
 * So each entry carries three things, and the order is the point:
 *
 *   headline  the consequence, in the reader's world. What goes wrong without
 *             this, or what it lets them stop doing. No feature names.
 *   detail    how it actually works, technically, in one sentence. Earns the
 *             headline — a claim with no mechanism behind it reads as marketing
 *             and this audience discounts it immediately.
 *   without   what they cannot currently answer. Phrased as the question they
 *             will be asked by somebody else, because that is when the absence
 *             hurts.
 *
 * Why not pure marketing
 * ----------------------
 * The buyer is a security architect or a model-risk function. "Ship agents with
 * confidence" to that reader is noise. But pure mechanism — "compares composite
 * fingerprints across scans" — never reaches the person who signs. The mix is
 * deliberate: consequence first, mechanism immediately after, so neither
 * audience is asked to take the other's word for it.
 *
 * What none of this may do
 * -----------------------
 * Claim anything about THEIR estate. No counts, no findings, no "you have N
 * ungoverned agents" — that would be a fabricated scan result with a sales
 * sentence attached, and it collapses the moment a prospect probes it. Every
 * sentence here is true of the product, not of the reader.
 */

export type Bundle = 'ARIAS Exposure' | 'ARIAS Evidence'

export interface BundleCopy {
  bundle: Bundle
  /** Shown as the heading of a teaser. The consequence, not the feature. */
  headline: string
  /** One sentence of mechanism, so the headline is not an assertion. */
  detail: string
  /** The question they cannot answer today. */
  without: string
  /** Column headers of the real table, for the blurred skeleton. Product
   *  vocabulary only — never values. */
  columns: string[]
}

/**
 * Keyed by entitlement feature key, so a surface looks up the copy by the same
 * key the gate refuses on and the two cannot drift apart.
 */
export const BUNDLE_COPY: Record<string, BundleCopy> = {
  certificates: {
    bundle: 'ARIAS Evidence',
    headline:
      'Prove an agent behaves the way it did when somebody signed off on it — '
      + 'without re-validating it from scratch every release.',
    detail:
      'Each agent gets a dated certificate recording its measured behaviour: '
      + 'the tools it holds, the model it calls, the data it reaches and how it '
      + 'delegates. Every later scan is compared against that record, so a new '
      + 'tool, a model swap or a widened data reach raises a verdict instead of '
      + 'passing unnoticed.',
    without:
      '"Is the agent in production still the one we approved?" — today that is '
      + 'answered by someone reading diffs.',
    columns: ['Agent', 'Status', 'Issued', 'Expires', 'Latest verdict'],
  },

  attestation: {
    bundle: 'ARIAS Evidence',
    headline:
      'Skip a validation cycle you have already paid for, defensibly.',
    detail:
      'A dated statement that nothing material has changed in an agent since it '
      + 'was certified — so the validation you ran then still describes what is '
      + 'running now. It refuses to assert where the measurement itself cannot '
      + 'be vouched for, which is what makes it worth putting in front of an '
      + 'auditor.',
    without:
      '"Can we reuse last quarter\'s sandbox result?" — without this, the safe '
      + 'answer is always no, and the calendar cost is weeks per agent.',
    columns: ['Agent', 'Certified', 'Claim', 'Measured against', 'Verdict'],
  },

  release_comparison: {
    bundle: 'ARIAS Evidence',
    headline:
      'Tell a release board exactly what changed about your agents, not about '
      + 'your code.',
    detail:
      'Compares two scans of the same repository and reports the difference at '
      + 'the agent level — tools gained or lost, model changes, delegation '
      + 'targets added, data reach widened — rather than as a file diff nobody '
      + 'can review for behaviour.',
    without:
      '"What changed in this release?" answered with a commit range, which is '
      + 'not an answer anyone can sign.',
    columns: ['Agent', 'Change', 'Before', 'After', 'Materiality'],
  },

  // Covers the whole BYOF pipeline, not just the framework half. A customer
  // imports a policy; ARIAS extracts the framework AND the detection rules
  // from it; scans then run on custom rules alone or on ARIAS plus custom; and
  // the result lands in a custom compliance posture tile. Custom Rules is a
  // STAGE in that chain rather than a separate power-user feature, which is
  // why it resolves on this key instead of one of its own — a second key
  // always granted beside the first is the fail-closed trap that cost
  // Telemetry Gaps its own entitlement.
  custom_framework: {
    bundle: 'ARIAS Evidence',
    headline:
      'Turn your own written policy into checks that run on every scan, and '
      + 'report against it like any published framework.',
    detail:
      'Import the policy document. ARIAS extracts the control framework and '
      + 'the detection rules from it, then scans against your rules alone or '
      + 'alongside its own, and reports the result as a compliance posture '
      + 'tile of your own. Coverage is marked mapped, unmapped or undetectable, '
      + 'so a control nothing can evidence is visible rather than quietly '
      + 'counted as a pass.',
    without:
      '"Show us against OUR policy, not a standard" — today that is a '
      + 'spreadsheet somebody maintains by hand, and nothing enforces it at '
      + 'commit time.',
    columns: ['Control', 'Source policy', 'Rule', 'Evidence', 'Coverage'],
  },

  compliance_posture: {
    bundle: 'ARIAS Evidence',
    headline:
      'Answer a regulator at control level, with the measurement behind every '
      + 'claim.',
    detail:
      'Maps what ARIAS detects onto EU AI Act, NIST AI RMF, ISO 42001, SOC 2, '
      + 'GDPR, OWASP LLM and MAESTRO at control granularity — and marks a '
      + 'control as unevidenced rather than compliant where nothing in your '
      + 'estate speaks to it.',
    without:
      '"Which article does this satisfy, and how do you know?" — the second '
      + 'half of that question is the one that ends audits badly.',
    columns: ['Framework', 'Control', 'Posture', 'Evidence', 'Gaps'],
  },

  attack_paths: {
    bundle: 'ARIAS Exposure',
    headline:
      'Know which agents are actually reachable by content you did not write — '
      + 'and stop testing the ones that are not.',
    detail:
      'Traces routes from externally-authored content to an agent that changes '
      + 'something, naming every hop and whether anything validates on the way. '
      + 'Says what it could not assess instead of reporting it as clear, so a '
      + 'route you skip is one you chose to skip.',
    without:
      '"Where would a prompt injection actually land?" — answered today by '
      + 'testing everything, or guessing.',
    columns: ['Origin', 'Agent', 'Verdict', 'Hops', 'Controls'],
  },

  permission_fit: {
    bundle: 'ARIAS Exposure',
    headline:
      'Catch agents holding far more access than their own description '
      + 'justifies, before someone else does.',
    detail:
      'Compares the access an agent actually holds against the role its own '
      + 'definition claims, and reports the gap with the evidence for both '
      + 'sides — so the finding is arguable with the team that owns the agent '
      + 'rather than a score they dispute.',
    without:
      '"Why does the summariser have write access to payments?" — a question '
      + 'that usually arrives after an incident.',
    columns: ['Agent', 'Stated role', 'Access held', 'Gap', 'Evidence'],
  },

  escalation_paths: {
    bundle: 'ARIAS Exposure',
    headline:
      'See the capability an agent can reach by asking another agent, not just '
      + 'what it was granted.',
    detail:
      'Walks the real delegation graph — delegation, A2A and MCP — and '
      + 'reconstructs the ordered path by which a low-privilege agent reaches a '
      + 'high-privilege one. The path is rendered hop by hop and highlightable '
      + 'on the topology, because the chain is the finding.',
    without:
      '"Could anything reach the production database indirectly?" — '
      + 'unanswerable from a permission list.',
    columns: ['Agent', 'Reaches', 'Via', 'Hops', 'Tier gained'],
  },

  objective_conflicts: {
    bundle: 'ARIAS Exposure',
    headline:
      'Find agent pairs pulling in opposite directions on the same decision '
      + 'before they do it in production.',
    detail:
      'Compares stated objectives across agents that touch the same concern and '
      + 'flags pairs pointing at opposing outcomes — the failure that produces '
      + 'no error, no exception and no alert, only a worse decision.',
    without:
      '"Why did the system approve and then revoke?" — the kind of behaviour '
      + 'that is reproduced for days before it is explained.',
    columns: ['Agent A', 'Agent B', 'Concern', 'Conflict', 'Confidence'],
  },

  permission_drift: {
    bundle: 'ARIAS Exposure',
    headline:
      'Hear about an agent gaining broader access the scan after it happens, '
      + 'not at the next audit.',
    detail:
      'Compares each agent\'s access tier against the baseline it held at its '
      + 'last scan and signals a widening — read to write, write to execute, '
      + 'scoped to unscoped — with the two measurements side by side.',
    without:
      '"When did this agent get delete rights?" — answered by git archaeology, '
      + 'if at all.',
    columns: ['Agent', 'Tier before', 'Tier now', 'Gained', 'First seen'],
  },
}

/** Copy for a feature key, or null where the key is not a bundled capability. */
export const copyFor = (feature: string): BundleCopy | null =>
  BUNDLE_COPY[feature] ?? null
